/**
 * ReadOnlyField - Display-only field for verification group
 * Shows extracted data for reference without editing capability
 */

import React from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Chip from '@mui/material/Chip';
import Tooltip from '@mui/material/Tooltip';
import { styled } from '@mui/material/styles';
import { ExtractedField } from '../../types';
import ConfidenceIndicator from './ConfidenceIndicator';
import TransformationPreview from './TransformationPreview';
import { useHighlighting } from '../../contexts/HighlightingContext';

const FieldContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  border: `1px solid ${theme.palette.divider}`,
  borderRadius: theme.shape.borderRadius,
  backgroundColor: theme.palette.grey[50],
  cursor: 'pointer',
  transition: theme.transitions.create(['border-color', 'background-color'], {
    duration: theme.transitions.duration.short,
  }),
  '&:hover': {
    borderColor: theme.palette.info.main,
    backgroundColor: theme.palette.info.light + '10',
  },
}));

const FieldHeader = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(1),
}));

const FieldLabel = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  color: theme.palette.text.primary,
  fontSize: '0.875rem',
}));

const FieldValue = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
  fontSize: '0.875rem',
  wordBreak: 'break-word',
  minHeight: '1.25rem',
}));

interface ReadOnlyFieldProps {
  field: ExtractedField;
  value: string;
  className?: string;
  /** Overrides the displayed label. Defaults to the raw extracted field name. */
  label?: string;
  /**
   * Replaces the default header badges (confidence + the "Reference" chip). The "Reference" chip
   * carries the review workspace's reference/new-information split, which has no meaning on the
   * Lens archive surface — so Lens supplies its own provenance + confidence pair instead of
   * rendering a second, contradictory set alongside them.
   */
  badges?: React.ReactNode;
  /**
   * Whether to append the required-field asterisk. Off for archived records: on a stored document
   * an asterisk reads as "required input", the same miscue as a disabled text box.
   */
  showRequiredMarker?: boolean;
}

const ReadOnlyField: React.FC<ReadOnlyFieldProps> = ({
  field,
  value,
  className,
  label,
  badges,
  showRequiredMarker = true,
}) => {
  const { actions: highlightingActions, state: highlightingState } = useHighlighting();

  const hasHighlight = !!field.boundingBoxes && field.boundingBoxes.length > 0;

  const handleClick = () => {
    // Only wired when `hasHighlight`; the guard keeps it safe if ever called directly.
    if (!hasHighlight) return;

    // For multi-page fields, find the page with the largest bounding box area
    const targetPage = field.boundingBoxes.length === 1 
      ? field.boundingBoxes[0].page
      : field.boundingBoxes.reduce((bestPage, bbox) => {
          const currentArea = bbox.width * bbox.height;
          const bestArea = bestPage.width * bestPage.height;
          return currentArea > bestArea ? bbox : bestPage;
        }).page;
    
    const currentPage = highlightingState.currentPage;
    
    // Navigate to page if different
    if (targetPage !== currentPage) {
      highlightingActions.setCurrentPage(targetPage);
      
      // Set active field after page change with a delay to allow viewport update
      setTimeout(() => {
        highlightingActions.setActiveField(field.id, field.boundingBoxes);
        // Scroll to the field location
        setTimeout(() => {
          scrollToField(field.boundingBoxes[0]);
        }, 100);
      }, 200);
    } else {
      // Same page - set highlight immediately
      highlightingActions.setActiveField(field.id, field.boundingBoxes);
      
      // Scroll to the field location
      setTimeout(() => {
        scrollToField(field.boundingBoxes[0]);
      }, 100);
    }
  };

  // Function to scroll to field location
  const scrollToField = (boundingBox: any) => {
    if (!boundingBox) return;
    
    const pdfContainer = document.querySelector('[data-testid="pdf-viewer-container"]');
    if (!pdfContainer) return;
    
    // Get viewport from highlighting context
    const viewport = highlightingState.pdfViewport;
    if (!viewport) return;
    
    // Calculate position
    const highlightY = boundingBox.y * viewport.height;
    const highlightX = boundingBox.x * viewport.width;
    
    // Scroll with padding
    const padding = 100;
    pdfContainer.scrollTo({
      top: Math.max(0, highlightY - padding),
      left: Math.max(0, highlightX - padding),
      behavior: 'smooth'
    });
  };

  const formatValue = (val: string, fieldType: string): string => {
    if (!val) return 'Not extracted';
    
    switch (fieldType) {
      case 'currency':
        // Ensure currency formatting
        if (!val.startsWith('$')) {
          const numValue = parseFloat(val.replace(/[^0-9.-]/g, ''));
          return isNaN(numValue) ? val : `$${numValue.toLocaleString('en-US', { minimumFractionDigits: 2 })}`;
        }
        return val;
      case 'date':
        // Handle both ISO date and ISO datetime formats
        try {
          // Check for ISO datetime format (2025-10-01T00:00:00.000Z)
          if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}/.test(val)) {
            const dateOnly = val.split('T')[0]; // Extract just the date part
            const [year, month, day] = dateOnly.split('-');
            const formatted = `${month}/${day}/${year}`;
            return formatted;
          }
          // Check for ISO date format (YYYY-MM-DD)
          else if (/^\d{4}-\d{2}-\d{2}$/.test(val)) {
            const [year, month, day] = val.split('-');
            const formatted = `${month}/${day}/${year}`;
            return formatted;
          } else {
            // For other formats, use regular Date parsing
            const date = new Date(val);
            const formatted = date.toLocaleDateString('en-US');
            return formatted;
          }
        } catch {
          return val;
        }
      default:
        return val;
    }
  };

  const displayValue = formatValue(value, field.fieldType);
  const isEmpty = !value || value.trim() === '';

  const fieldLabel = label ?? field.name;

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handleClick();
    }
  };

  return (
    <Tooltip
      title={hasHighlight ? "Show this field's location in the PDF" : 'PDF highlighting not available'}
      placement="top"
      arrow
    >
      {/*
        Keyboard operability (WCAG 2.2 AA — 2.1.1 Keyboard, Level A). This container is the only
        interactive affordance in the Lens record panel, and it used to be a bare `div` with an
        `onClick`: reachable by mouse only, and the wrapping Tooltip unreachable with it. It now
        takes focus and responds to Enter/Space when there is actually a highlight to show, and
        stays a plain non-focusable container when there is not — so a keyboard user never tabs
        through rows that do nothing.
      */}
      <FieldContainer
        className={className}
        id={`readonly-field-${field.id}`}
        {...(hasHighlight
          ? {
              onClick: handleClick,
              onKeyDown: handleKeyDown,
              role: 'button',
              tabIndex: 0,
              'aria-label': `Show ${fieldLabel} in the document`,
            }
          : { sx: { cursor: 'default', '&:hover': { borderColor: 'divider' } } })}
      >
        <FieldHeader>
          <FieldLabel>
            {fieldLabel}
            {showRequiredMarker && field.required && ' *'}
          </FieldLabel>
          <Box display="flex" alignItems="center" gap={1}>
            {badges ?? (
              <>
                <ConfidenceIndicator
                  confidence={field.confidence}
                  variant="chip"
                  size="small"
                />
                <Chip
                  label="Reference"
                  size="small"
                  color="info"
                  variant="outlined"
                />
              </>
            )}
          </Box>
        </FieldHeader>

        <FieldValue
          sx={{
            fontStyle: isEmpty ? 'italic' : 'normal',
            color: isEmpty ? 'text.disabled' : 'text.secondary',
          }}
        >
          {displayValue}
        </FieldValue>

        {/* Inline transformation preview (UX4): shown when the normalize pipeline changed the extracted value */}
        {field.rawValue != null && <TransformationPreview rawValue={field.rawValue} />}
      </FieldContainer>
    </Tooltip>
  );
};

export default ReadOnlyField;